/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { SoundingsCollection } from '@opengeoweb/shared';
import type { CoreAppStore } from '@opengeoweb/store';
import { uiSelectors, uiTypes, useSetupDialog } from '@opengeoweb/store';
import { LocationsMenu } from './LocationsMenu';
import { useGetCollectionLocations } from '../../api/edr-api/hooks';
import { soundingsActions } from '../../store/reducer';
import {
  getLocationSelections,
  getSelectedSourcesAmount,
  getSelectedSourcesIds,
  getSoundingsCollections,
} from '../../store/selectors';
import type { SoundingsRootState } from '../../store/types';

interface Props {
  soundingsCollections?: SoundingsCollection[];
}

export const LocationsMenuConnect = ({
  soundingsCollections,
}: Props): React.ReactElement => {
  const dispatch = useDispatch();
  const { isDialogOpen, onCloseDialog, dialogOrder, setDialogOrder } =
    useSetupDialog(uiTypes.DialogTypes.SoundingsLocations);

  const panelId = useSelector<CoreAppStore, string | undefined>((store) =>
    uiSelectors.getDialogMapId(store, uiTypes.DialogTypes.SoundingsLocations),
  );
  const locationSelections = useSelector((state: SoundingsRootState) =>
    getLocationSelections(state, panelId),
  );
  const selectedSourceIds = useSelector((state: SoundingsRootState) =>
    getSelectedSourcesIds(state, panelId),
  );
  const sourcesAmount = useSelector((state: SoundingsRootState) =>
    getSelectedSourcesAmount(state, panelId),
  );
  const soundingsCollectionsFromStore = useSelector(
    (state: SoundingsRootState) => getSoundingsCollections(state),
  );
  const collections = soundingsCollections ?? soundingsCollectionsFromStore;

  React.useEffect(() => {
    if (soundingsCollections && soundingsCollections.length > 0) {
      dispatch(soundingsActions.setSoundingsCollections(soundingsCollections));
    }
  }, [dispatch, soundingsCollections]);

  const { data, isPending, error } = useGetCollectionLocations(
    collections,
    isDialogOpen,
  );

  const services = useMemo(() => {
    return (
      data?.filter((service) => selectedSourceIds.includes(service.id)) || []
    );
  }, [data, selectedSourceIds]);

  return (
    <LocationsMenu
      error={error}
      isLoading={isPending}
      isOpen={isDialogOpen}
      onClose={onCloseDialog}
      onMouseDown={setDialogOrder}
      order={dialogOrder}
      selectedLocations={locationSelections}
      selectedSourcesAmount={sourcesAmount}
      services={services}
      updateSelectedLocation={(sourceId: string, locationId?: string) => {
        dispatch(
          soundingsActions.updateSelectedLocation({
            sourceId,
            location: locationId,
            panelId,
          }),
        );
      }}
    />
  );
};
